import React, { useMemo, useState } from "react"
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Radio,
  RadioGroup,
  TextField,
  Theme,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { useTranslation } from "react-i18next"

import { useAppContext } from "../../app-context/use-app-context"
import { useChatMutations } from "../hooks/use-chat-mutations"
import { useGetOrgMembershipsQuery } from "../../memberships/api"

type Props = {
  open: boolean
  orgId: string
  onClose: () => void
  onCreated?: (chatId: string) => void
}

const CreateChatDialog = ({ open, orgId, onClose, onCreated }: Props) => {
  const classes = useStyles()
  const { t } = useTranslation("chat")
  const { t: common } = useTranslation("translation")

  const {
    appContext: { mainContextList },
  } = useAppContext()

  const currentUserId = mainContextList.find((ctx) => ctx.type === "user")?.id

  const { createChat } = useChatMutations()

  const { data: activeMemberships = [] } = useGetOrgMembershipsQuery(orgId)

  const members = useMemo(
    () => activeMemberships.filter((m) => m.userId !== currentUserId),
    [activeMemberships, currentUserId]
  )

  const [type, setType] = useState<"dm" | "group">("dm")
  const [groupName, setGroupName] = useState("")
  const [search, setSearch] = useState("")
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const filteredMembers = useMemo(() => {
    const term = search.toLowerCase()

    return members.filter(
      (m) => `${m.firstname} ${m.lastname}`.toLowerCase().includes(term) || m.userEmail.toLowerCase().includes(term)
    )
  }, [members, search])

  const toggleUser = (userId: string) => {
    if (type === "dm") {
      setSelectedUsers([userId])
      return
    }

    setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((x) => x !== userId) : [...prev, userId]))
  }

  const canCreate =
    type === "dm" ? selectedUsers.length === 1 : selectedUsers.length >= 2 && groupName.trim().length > 0

  const reset = () => {
    setType("dm")
    setGroupName("")
    setSelectedUsers([])
    setSearch("")
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleCreate = async () => {
    if (!canCreate) return

    setLoading(true)

    try {
      const chat = await createChat({
        type,
        orgId,
        name: type === "group" ? groupName : "",
        createdBy: currentUserId!,
        participantIds: selectedUsers,
      })

      if (chat) {
        onCreated?.(chat.id)
      }

      handleClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("createChatDialog.title")}</DialogTitle>

      <DialogContent>
        <RadioGroup
          value={type}
          onChange={(e) => {
            setSelectedUsers([])
            setGroupName("")
            setType(e.target.value as "dm" | "group")
          }}
          row
        >
          <FormControlLabel value="dm" control={<Radio />} label={t("createChatDialog.dmOptionLabel")} />

          <FormControlLabel value="group" control={<Radio />} label={t("createChatDialog.groupOptionLabel")} />
        </RadioGroup>

        {type === "group" && (
          <TextField
            fullWidth
            label={t("createChatDialog.groupNameLabel")}
            variant="outlined"
            margin="normal"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        )}

        <TextField
          fullWidth
          variant="outlined"
          margin="normal"
          label={t("searchPeopleLabel")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Typography variant="subtitle2" className={classes.sectionTitle}>
          {common("members")}
        </Typography>

        <List className={classes.list}>
          {filteredMembers.map((member) => (
            <ListItem key={member.userId} button onClick={() => toggleUser(member.userId)}>
              <Checkbox checked={selectedUsers.includes(member.userId)} />

              <ListItemText primary={`${member.firstname} ${member.lastname}`} secondary={member.userEmail} />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{common("cancel")}</Button>

        <Button color="primary" variant="contained" disabled={!canCreate || loading} onClick={handleCreate}>
          {common("create")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },

  list: {
    maxHeight: 320,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
}))

export default CreateChatDialog
